import {useCallback, useEffect, useRef} from 'react'
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom'

import {getSingleBuildArg} from '../../../../../rest/builds'
import {getBuildLocator} from '../../../../../rest/locators'
import {restApi} from '../../../../../services/rest'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import {BUILD_FINISHED} from '../../../../../utils/subscriptionEvents'
import {getTabParamsKey} from '../../../../../utils/tabs'

function useSelectedJob(jobId: number) {
  const [searchParams] = useSearchParams()
  const {
    isLoading,
    tabs,
    refetch: refetchTabs,
  } = restApi.endpoints.getTabs.useQuery(
    {key: getTabParamsKey({buildId: jobId}), cacheTabs: true},
    {
      selectFromResult: ({isLoading, data}) => ({isLoading, tabs: data}),
    },
  )
  const location = useLocation()
  const navigate = useNavigate()
  const {isFinished, headInternalId, refetch} = restApi.endpoints.getBuildNormalized.useQuery(
    getSingleBuildArg(getBuildLocator(jobId), {
      withBuildTypeDetails: true,
    }),
    {
      selectFromResult: ({data}) => {
        const run = data?.entities?.builds?.[data?.result!]
        const headId = run?.buildType
        const {result, entities} = data ?? {}
        return {
          isFinished: entities?.builds?.[result!]?.state === 'finished',
          headInternalId: data?.entities?.buildTypes?.[headId!]?.internalId,
        }
      },
    },
  )
  const iframe = useRef<HTMLIFrameElement>(null)
  const focusIframe = useCallback(() => iframe.current?.contentWindow?.focus(), [])

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowRight':
        case 'ArrowDown':
        case 'ArrowLeft':
          focusIframe()
          break
        default:
      }
    }
    document.addEventListener('keydown', listener)
    return () => document.removeEventListener('keydown', listener)
  })

  useEffect(
    () =>
      headInternalId
        ? subscribeOnBuildTypeEvents(headInternalId, [BUILD_FINISHED], () => {
            refetchTabs()
            refetch()
          })
        : undefined,
    [headInternalId, refetch, refetchTabs],
  )

  return {
    isFinished,
    searchParams,
    navigate,
    location,
    isLoading,
    tabs,
    iframe,
    focusIframe,
  }
}

export default useSelectedJob
